import React, { useEffect, useState, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import AdminDataService from "../services/admin.service";
import RechercheDataService from "../services/recherche.service";
import ManagerDataService from "../services/manager.service";
import { setAuthToken } from "../http-common";
import ProfileModal from "./profile-modal.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

const MonProfil = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profil, setProfil] = useState({
    nom: "",
    prenom: "",
    sexe: "",
    date_naiss: "",
    nationalite: "",
    residence: "",
    tel: ""
  });
  const [subTerrs, setSubTerrs] = useState([]);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [habilitations, setHabilitations] = useState([]);
  const [roles, setRoles] = useState(null);
  const isAdmin = useMemo(() => {
    const a = roles?.admin;
    return a === true || a === 1 || a === "1";
  }, [roles]);
  const groupedHabilitations = useMemo(() => {
    const groups = habilitations.reduce((acc, h) => {
      const k = h.libelle_structure || "—";
      (acc[k] = acc[k] || []).push(h);
      return acc;
    }, {});
    const entries = Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
    entries.forEach(([_, arr]) => arr.sort((a, b) => String(a.libelle_service || "").localeCompare(String(b.libelle_service || ""))));
    return entries;
  }, [habilitations]);

  const loadProfil = async () => {
    setError("");
    try {
      const res = await AdminDataService.getUserNameByEmail(user.email);
      const p = res?.data || {};
      setProfil({
        nom: p.nom || "",
        prenom: p.prenom || "",
        sexe: (p.sexe || "").toUpperCase(),
        date_naiss: p.date_naiss ? String(p.date_naiss).slice(0, 10) : "",
        nationalite: p.nationalite || "",
        residence: p.residence || "",
        tel: p.tel || ""
      });
      try {
        const subRes = await AdminDataService.list('sub-terr');
        setSubTerrs(subRes?.data || []);
      } catch {}
      // Charger informations médecin (titre, matricule, spécialités) si existantes
      try {
        const resDoc = await RechercheDataService.showDoctor2(user.email);
        const arr = resDoc?.data || [];
        setDoctorInfo(arr && arr.length ? arr[0] : null);
      } catch {}
      // Charger mes habilitations (liens Utilisateur/Service actifs)
      try {
        const us = await AdminDataService.getUserServiceByEmail(user.email);
        setHabilitations(Array.isArray(us?.data) ? us.data : []);
      } catch (e) {
        console.error("getUserServiceByEmail failed", e?.response?.data || e?.message);
      }
      // Charger les rôles (admin, manager, secretaire, medecin)
      try {
        const r = await ManagerDataService.habilitation(user.email);
        const arr = Array.isArray(r?.data) ? r.data : [];
        setRoles(arr.length ? arr[0] : null);
      } catch (e) {
        console.error("manager.habilitation failed", e?.response?.data || e?.message);
      }
      if (!p || !p.nom || String(p.nom).trim() === "") {
        setShowProfileModal(true);
      }
    } catch {
      setError("Impossible de charger le profil");
      setShowProfileModal(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        });
        if (token && token.split(".").length === 3) setAuthToken(token);
      } catch {}
      await loadProfil();
    };
    if (user?.email) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  return (
    <div className="container-fluid">
      {/* Bannière (même style que ConfirmRdv) */}
      <div className="row" style={{ backgroundColor: "#6f42c1", color: "white", padding: "20px" }}>
        <span style={{ fontSize: "1.5rem" }} className="lead">
          <FontAwesomeIcon icon={solid('user')} className="me-2" />
          <b>Mon profil</b>
        </span>
      </div>

      {/* Contenu (même organisation de blocs que ConfirmRdv) */}
      <div className="row" style={{ backgroundColor: "#E6EAF7", padding: "40px" }}>
        <div className="col-6" style={{ backgroundColor: "white", padding: "40px" }}>
          {loading && <div>Chargement...</div>}
          {error && <div className="text-danger mb-2">{error}</div>}
          {!loading && (
            <>
              <span style={{ fontSize: "1.2rem" }} className="lead"><b>Mes informations</b></span>
              <hr />
              <ul className="list-unstyled">
                <li className="lead"><b>Nom:</b> {profil.nom || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Prénom:</b> {profil.prenom || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Sexe:</b> {profil.sexe || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Date de naissance:</b> {profil.date_naiss || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Nationalité:</b> {profil.nationalite || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Résidence:</b> {(subTerrs.find(st => String(st.id) === String(profil.residence))?.libelle_sub_terr) || <span className="text-muted">—</span>}</li>
                <li className="lead"><b>Téléphone:</b> {profil.tel || <span className="text-muted">—</span>}</li>
              </ul>

              <div className="mt-3">
                <button className="btn btn-primary" onClick={() => setShowProfileModal(true)}>
                  Mettre à jour mon profil
                </button>
              </div>
            </>
          )}
        </div>
        <div className="col-6" style={{ backgroundColor: "white", padding: "40px" }}>
          {loading && <div>Chargement...</div>}
          {error && <div className="text-danger mb-2">{error}</div>}
          {!loading && (
            <>
              <span style={{ fontSize: "1.2rem" }} className="lead"><b>Mes habilitations</b></span>
              <hr />
              {isAdmin && (
                <span className="ms-2 badge bg-warning text-dark">
                  <FontAwesomeIcon icon={solid('user-shield')} className="me-1" />
                  Admin
                </span>
              )}
              {doctorInfo && (
                <div className="p-3 mt-2" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                  <div>
                    <span className="badge bg-success">
                      <FontAwesomeIcon icon={solid('user-doctor')} className="me-1" />
                      {doctorInfo.titre || '—'}
                    </span>
                  </div>
                  <ul className="list-unstyled mb-0 mt-2">
                    <li className="lead"><b>Matricule:</b> {doctorInfo.matricule || <span className="text-muted">—</span>}</li>
                    <li className="lead"><b>Spécialité(s):</b> {doctorInfo.spec || <span className="text-muted">—</span>}</li>
                  </ul>
                </div>
              )}
              <div className="p-3 mt-2" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                {(!habilitations || !habilitations.length) && (
                  <div className="text-muted">Aucun lien utilisateur/service actif</div>
                )}
                {groupedHabilitations && groupedHabilitations.length > 0 && (
                  <div>
                    {groupedHabilitations.map(([structure, items]) => (
                      <div key={structure} className="mb-3">
                        <div className="text-uppercase fw-bold text-secondary small">{structure}</div>
                        <ul className="list-unstyled mb-0">
                          {items.map((h, idx) => (
                            <li key={h.id} className={idx === items.length - 1 ? "py-1" : "py-1 border-bottom"}>
                              <span className="lead"><b>{h.libelle_service || <span className="text-muted">—</span>}</b></span>
                              <span className="ms-2 badge bg-primary">
                                {Number(h.recette) === 1 && (
                                  <FontAwesomeIcon icon={solid('dollar-sign')} className="me-1" />
                                )}
                                {h.role || '—'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Profil */}
      {showProfileModal && (
        <ProfileModal
          isOpen={showProfileModal}
          email={(user?.email || '').trim()}
          canClose={Boolean(profil?.nom)}
          onClose={() => setShowProfileModal(false)}
          onSaved={async () => {
            setShowProfileModal(false);
            setLoading(true);
            await loadProfil();
          }}
        />
      )}
    </div>
  );
};

export default MonProfil;